/**
 * useCouncilStream — manages the WebSocket lifecycle for /ai/council/stream:
 *   • Opens the WS, sends the question + selected personas + moderator
 *   • Translates each server frame ("member_start", "member_done",
 *     "moderator_done", "session_saved", "error") into local state
 *   • Falls back to POST /ai/council/discuss if the WS handshake fails
 *
 * Returns:
 *   { liveAnswers, liveStage, running, result, history, convene, reloadHistory }
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the convene WS callback (event-triggered, not an effect). */

export default function useCouncilStream() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [liveAnswers, setLiveAnswers] = useState([]);
  const [liveStage, setLiveStage] = useState('idle');

  // refs mirror state so the WS onmessage handler can read fresh values
  const liveAnswersRef = useRef([]);
  const liveStageRef = useRef('idle');
  useEffect(() => { liveAnswersRef.current = liveAnswers; }, [liveAnswers]);
  useEffect(() => { liveStageRef.current = liveStage; }, [liveStage]);

  const reloadHistory = useCallback(async () => {
    try {
      const { data } = await api.get('/ai/council/sessions');
      setHistory(data);
    } catch (err) {
      // Non-fatal: history is a side-panel, the rest of the page can run
      // without it. Log so silent regressions surface in dev tools.
      console.warn('useCouncilStream: history reload failed', err?.response?.status, err?.message);
    }
  }, []);

  // Initial load
  useEffect(() => { reloadHistory(); }, [reloadHistory]);

  const convene = useCallback(async ({ question, personas, moderator }) => {
    setRunning(true);
    setResult(null);
    setLiveAnswers([]);
    setLiveStage('opening');

    const wsUrl = (() => {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const proto = base.startsWith('https') ? 'wss' : 'ws';
      const host = base.replace(/^https?:\/\//, '').replace(/\/$/, '');
      return `${proto}://${host}/api/ai/council/stream`;
    })();

    let socket;
    try {
      socket = new WebSocket(wsUrl);
    } catch (e) {
      toast.error('Could not open council stream.');
      setRunning(false);
      return;
    }

    const fallback = async () => {
      try {
        const { data } = await api.post('/ai/council/discuss', {
          question, personas, moderator,
        });
        setResult(data);
        reloadHistory();
        toast.success(`Council deliberated. ${personas.length} members + ${moderator} moderated.`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Council convening failed.');
      } finally {
        setRunning(false);
      }
    };

    socket.onopen = () => {
      socket.send(JSON.stringify({ question, personas, moderator, rounds: 1 }));
    };

    socket.onmessage = (ev) => {
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch (err) {
        // Skip unparseable frames — server only emits JSON, so this
        // likely means a partial frame. Log once so it shows up if
        // the server ever starts sending malformed data.
        console.warn('useCouncilStream: dropped unparseable frame', err?.message);
        return;
      }
      if (msg.type === 'started') {
        setLiveStage('round_1');
      } else if (msg.type === 'member_start') {
        setLiveAnswers((cur) => [...cur, { persona: msg.persona, round: msg.round, status: 'thinking', answer: '' }]);
      } else if (msg.type === 'member_done') {
        setLiveAnswers((cur) =>
          cur.map((a) => (a.persona === msg.persona && a.round === msg.round
            ? { ...a, status: 'done', answer: msg.answer }
            : a))
        );
      } else if (msg.type === 'round_complete') {
        setLiveStage(`round_${msg.round + 1}`);
      } else if (msg.type === 'moderator_start') {
        setLiveStage('moderating');
      } else if (msg.type === 'moderator_done') {
        setResult({
          synthesis: msg.synthesis,
          answers: liveAnswersRef.current.filter((a) => a.status === 'done')
            .map((a) => ({ persona: a.persona, answer: a.answer })),
          moderator,
        });
      } else if (msg.type === 'session_saved') {
        setLiveStage('saved');
        setResult((cur) => ({ ...(cur || {}), ...msg.record, _saved: true }));
        toast.success(`Council saved · ${msg.council_id}.`);
        reloadHistory();
      } else if (msg.type === 'error') {
        toast.error(msg.detail || 'Stream error');
        setLiveStage('error');
      }
    };

    socket.onerror = () => {
      toast.info('Live stream unavailable — falling back to standard convening.');
      try { socket.close(); } catch (_) { /* ignore */ }
      fallback();
    };

    socket.onclose = (ev) => {
      setRunning(false);
      if (ev.code !== 1000 && ev.code !== 1005 && liveStageRef.current !== 'saved') {
        fallback();
      }
    };
  }, [reloadHistory]);

  return { liveAnswers, liveStage, running, result, history, convene, reloadHistory };
}
